// The Passes tab of Pricing & promotions (spec/13 §18; F-PRC-01/-02/-06) — until 2026-09-29 its own
// screen at `…/passes`, which now redirects here. The venue's pass catalogue as rows — each with a
// 24-hour "when it works" band tinted by its first station class, its price, duration, validity and
// today's count, and a Sell or Grant action. "New pass" is a three-step Stepper in a centred dialog (Basics →
// Where and when → Validity) with a live preview; Sell or Grant opens a dialog that finds the player.
// Making a pass needs `passes:manage`; granting one `passes:grant`; selling one `orders:create`. A
// member without them sees the rows read-only.
//
// **Sell** goes through the Sell screen (F-POS-01, 2026-09-30): it opens with this pass in the cart and the
// player attached, and checkout takes the payment — so every counter pass sale is a sale in Orders, with its
// tenders (split included), its receipt and any discount code. The old direct `sellPass` call, which issued
// a holding with no sale behind it, is no longer used here. **Grant** (free, with a reason) stays in the dialog.

import type { CreatePassProductRequest, OrganizationPlayer, PassHolding, PassProduct, PassScope, PassValidity, UpdatePassProductRequest } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Avatar, Badge, Button, Chip, Dialog, EmptyState, FormSection, SegmentedControl, SelectField, Skeleton, Stepper, Switch, TextField, useToast } from '@clavian/ui-web';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { useAccess, useMyVenues } from '../../api/queries';
import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { LoadFailure } from '../../shell/LoadFailure';
import { NavIcon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { swatchStyle, tokenColor } from '../../stations/fields';
import { useStationClasses } from '../../stations/queries';
import { CardsSkeleton, Forbidden, Notice, SearchField } from '../../ui';
import { minorToMajor, moneyFrom } from '../money';
import { useServerErrors } from '../server-errors';
import { ScopeChoice, type ScopeChoiceKind } from '../pricing/ScopeChoice';
import { usePassHoldings, useCreatePassProduct, useGrantPass, usePassProducts, useRefundPassHolding, useUpdatePassProduct, useVoidPassHolding } from './queries';
import { onCounterShelf, validitySummary } from './shelf';

import './passes.css';

const PASSES_MANAGE = 'passes:manage';
const PASSES_GRANT = 'passes:grant';
const PASSES_REFUND = 'passes:refund';
const PASSES_VOID = 'passes:void';
const ORDERS_CREATE = 'orders:create';

const LABELS = ['standard', 'day', 'night', 'weekend', 'special', 'limited'] as const;
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun', 'holiday'] as const;

export function PassesTab(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const organizationId = venue.organizationId;
  const access = useAccess({ venueId: venue.id });
  const canManage = access.status === 'ready' && access.can(PASSES_MANAGE);
  const canGrant = access.status === 'ready' && access.can(PASSES_GRANT);
  const canSell = access.status === 'ready' && access.can(ORDERS_CREATE);
  const canRefund = access.status === 'ready' && access.can(PASSES_REFUND);
  const canVoid = access.status === 'ready' && access.can(PASSES_VOID);

  const products = usePassProducts(organizationId, venue.id);
  const classes = useStationClasses(organizationId, venue.id);
  const classColor = useMemo(() => {
    const map = new Map<string, string>();
    for (const klass of classes.data ?? []) map.set(klass.id, klass.colorToken);
    return map;
  }, [classes.data]);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PassProduct | null>(null);
  const [selling, setSelling] = useState<PassProduct | null>(null);
  const [holdingsOpen, setHoldingsOpen] = useState(false);

  let body: ReactNode;
  if (products.data === undefined) {
    if (products.isError && problemCode(products.error) === PROBLEM.forbidden) {
      body = <Forbidden message={t('passes.notPermitted')} />;
    } else {
      body = products.isError ? <LoadFailure error={products.error} message={t('passes.unavailable')} onRetry={() => void products.refetch()} /> : <CardsSkeleton count={3} height="6rem" />;
    }
  } else if (products.data.length === 0) {
    body = (
      <EmptyState
        icon={<NavIcon name="passes" />}
        title={t('passes.emptyTitle')}
        description={t('passes.empty')}
        action={
          canManage ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              {t('passes.new')}
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    body = (
      <ul className="console-pass-list" aria-label={t('passes.listLabel')}>
        {products.data.map((product) => (
          <li key={product.id}>
            <PassRow product={product} classColor={classColor} canSell={canSell} canGrant={canGrant} canManage={canManage} onSellGrant={() => setSelling(product)} onEdit={() => setEditing(product)} />
          </li>
        ))}
      </ul>
    );
  }

  const actions =
    canRefund || canVoid || canManage ? (
      <div className="console-pricing-actions">
        {canRefund || canVoid ? (
          <Button variant="secondary" onClick={() => setHoldingsOpen(true)}>
            {t('passes.holdings.action')}
          </Button>
        ) : null}
        {/* With no pass yet the empty state offers New pass; the bar does not offer it a second time. */}
        {canManage && products.data !== undefined && products.data.length > 0 ? (
          <Button variant="primary" onClick={() => setCreating(true)}>
            {t('passes.new')}
          </Button>
        ) : null}
      </div>
    ) : null;

  return (
    <div className="console-pricing-panel">
      <div className="console-pricing-bar">
        <p className="console-subtle">{t('passes.intro')}</p>
        {actions}
      </div>
      {access.status === 'ready' && !canManage ? <Notice role="note">{t('passes.readOnly')}</Notice> : null}
      {body}

      <Dialog open={creating} onOpenChange={setCreating} size="lg" dirty="auto" title={t('passes.create.title')} description={t('passes.create.hint')}>
        {creating ? (
          <PassProductFlow
            organizationId={organizationId}
            venueId={venue.id}
            currency={venue.currency ?? 'INR'}
            classOptions={(classes.data ?? []).map((c) => ({ id: c.id, name: c.name, colorToken: c.colorToken }))}
            onDone={() => setCreating(false)}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => (open ? undefined : setEditing(null))}
        size="lg"
        dirty="auto"
        eyebrow={editing?.version.name}
        title={t('passes.edit.title')}
        description={t('passes.edit.hint')}
      >
        {editing !== null ? (
          <PassProductFlow
            organizationId={organizationId}
            venueId={venue.id}
            currency={venue.currency ?? 'INR'}
            classOptions={(classes.data ?? []).map((c) => ({ id: c.id, name: c.name, colorToken: c.colorToken }))}
            editing={editing}
            onDone={() => setEditing(null)}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={selling !== null}
        onOpenChange={(open) => {
          if (!open) setSelling(null);
        }}
        size="md"
        eyebrow={t('passes.sellGrant')}
        title={selling === null ? '' : selling.version.name}
        description={t('passes.sell.hint')}
      >
        {selling === null ? null : (
          <SellGrantForm
            organizationId={organizationId}
            venueId={venue.id}
            venueSlug={venue.slug}
            product={selling}
            canSell={canSell}
            canGrant={canGrant}
            onDone={() => setSelling(null)}
          />
        )}
      </Dialog>
      <Dialog open={holdingsOpen} onOpenChange={setHoldingsOpen} size="md" eyebrow={t('passes.holdings.action')} title={t('passes.holdings.title')} description={t('passes.holdings.hint')}>
        {holdingsOpen ? <HoldingsManager organizationId={organizationId} venueId={venue.id} canRefund={canRefund} canVoid={canVoid} /> : null}
      </Dialog>
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// A pass row
// -------------------------------------------------------------------------------------------------

function PassRow({
  product,
  classColor,
  canSell,
  canGrant,
  canManage,
  onSellGrant,
  onEdit,
}: {
  readonly product: PassProduct;
  readonly classColor: ReadonlyMap<string, string>;
  readonly canSell: boolean;
  readonly canGrant: boolean;
  readonly canManage: boolean;
  readonly onSellGrant: () => void;
  readonly onEdit: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const version = product.version;
  const firstClass = version.stationClassIds[0];
  const tint = firstClass === undefined ? undefined : classColor.get(firstClass);

  return (
    // On sale, the card carries its class's colour as the rail along its top (the floor's group swatch); off sale it is
    // hatched like a PC that cannot play (ADR-0043).
    <article
      className={`console-pass-card console-rail${product.active ? '' : ' is-inactive'}`}
      data-rail={product.active ? 'class' : 'off'}
      style={tint === undefined ? undefined : ({ '--console-rail-class': tokenColor(tint as never) } as CSSProperties)}
    >
      <div className="console-pass-card__main">
        <div className="console-pass-card__head">
          <h3 className="console-pass-card__name">{version.name}</h3>
          {version.label === 'standard' ? null : <Badge tone="neutral">{t(`passes.label.${version.label}`)}</Badge>}
          {product.active ? null : (
            <Badge tone="warn" dot>
              {t('passes.inactive')}
            </Badge>
          )}
        </div>
        <WhenItWorksBand windows={version.windows} tint={tint} />
      </div>
      <dl className="console-pass-facts">
        <div>
          <dt>{t('passes.field.price')}</dt>
          <dd className="console-pass-facts__price console-num">{format.money(version.price)}</dd>
        </div>
        <div>
          <dt>{t('passes.field.duration')}</dt>
          <dd>{t('passes.minutes', { count: Math.round(version.durationSeconds / 60) })}</dd>
        </div>
        <div>
          <dt>{t('passes.field.validity')}</dt>
          <dd>{validitySummary(t, version.validity)}</dd>
        </div>
        <div>
          <dt>{t('passes.field.today')}</dt>
          <dd className="console-num">{product.soldToday}</dd>
        </div>
      </dl>
      <div className="console-pass-card__actions">
        {canManage ? (
          <Button variant="ghost" size="sm" onClick={onEdit} label={t('passes.edit.actionNamed', { name: version.name })}>
            {t('passes.edit.action')}
          </Button>
        ) : null}
        {canSell || canGrant ? (
          <Button variant="secondary" size="sm" onClick={onSellGrant} disabled={!product.active}>
            {t('passes.sellGrant')}
          </Button>
        ) : null}
      </div>
    </article>
  );
}

const MINUTES_PER_DAY = 1440;

/** The 24-hour "when it works" band: lit where a window is open, or all day for "any time". */
function WhenItWorksBand({ windows, tint }: { readonly windows: PassProduct['version']['windows']; readonly tint?: string }): ReactNode {
  const { t } = useTranslation('console');
  const segments = useMemo(() => segmentsOf(windows), [windows]);
  const style = tint === undefined ? undefined : swatchStyle(tint as never);
  const days = useMemo(() => [...new Set(windows.flatMap((w) => w.days))], [windows]);

  return (
    <div className="console-pass-when">
      <div className="console-pass-band" role="img" aria-label={windows.length === 0 ? t('passes.anytime') : t('passes.whenAria')}>
        {segments.map((seg, index) => (
          <span
            key={index}
            className="console-pass-band__lit"
            style={{ left: `${(seg.from / MINUTES_PER_DAY) * 100}%`, width: `${((seg.to - seg.from) / MINUTES_PER_DAY) * 100}%`, ...(style ?? {}) }}
          />
        ))}
      </div>
      <div className="console-pass-band__scale" aria-hidden="true">
        <span>00</span>
        <span>06</span>
        <span>12</span>
        <span>18</span>
        <span>24</span>
      </div>
      <div className="console-pass-when__meta">
        {windows.length === 0 ? (
          <span className="console-pass-when__any">{t('passes.anytime')}</span>
        ) : (
          days.map((day) => (
            <span key={day} className="console-pass-day">
              {t(`passes.day.${day}`)}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

interface Segment {
  from: number;
  to: number;
}

/** The lit minute-ranges of a 24h day: every window's `[start,end)`, split at midnight and merged. */
function segmentsOf(windows: PassProduct['version']['windows']): Segment[] {
  if (windows.length === 0) return [{ from: 0, to: MINUTES_PER_DAY }];
  const raw: Segment[] = [];
  for (const window of windows) {
    const start = minuteOf(window.start);
    const end = minuteOf(window.end);
    if (start === end) continue;
    if (end > start) raw.push({ from: start, to: end });
    else {
      raw.push({ from: start, to: MINUTES_PER_DAY });
      raw.push({ from: 0, to: end });
    }
  }
  raw.sort((a, b) => a.from - b.from);
  const merged: Segment[] = [];
  for (const seg of raw) {
    const last = merged.at(-1);
    if (last !== undefined && seg.from <= last.to) last.to = Math.max(last.to, seg.to);
    else merged.push({ ...seg });
  }
  return merged;
}

function minuteOf(hhmm: string): number {
  const [h, m] = hhmm.split(':');
  return (Number(h) || 0) * 60 + (Number(m) || 0);
}

// -------------------------------------------------------------------------------------------------
// Sell / Grant
// -------------------------------------------------------------------------------------------------

function SellGrantForm({
  organizationId,
  venueId,
  venueSlug,
  product,
  canSell,
  canGrant,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly venueSlug: string;
  readonly product: PassProduct;
  readonly canSell: boolean;
  readonly canGrant: boolean;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const { show } = useToast();
  const api = useApi();
  const navigate = useNavigate();
  const grant = useGrantPass(organizationId, venueId);

  // Sold at the counter only when the Sell screen would list it (the server re-checks at checkout).
  const sellable = canSell && onCounterShelf(product, venueId, new Date());
  const [q, setQ] = useState('');
  const [player, setPlayer] = useState<OrganizationPlayer | null>(null);
  const [mode, setMode] = useState<'sell' | 'grant'>(sellable || !canGrant ? 'sell' : 'grant');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const search = useQuery({
    queryKey: ['passes', 'player-search', organizationId, q],
    queryFn: () => api.listPlayers({ path: { organizationId }, query: { q, limit: 8 } }),
    enabled: q.trim().length >= 2 && player === null,
  });

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(undefined);
    if (player === null) {
      setError(t('passes.sell.pickPlayer'));
      return;
    }
    if (mode === 'sell') {
      // A sale, not a holding: checkout on the Sell screen takes the payment, and the pass is issued when it is paid.
      onDone();
      void navigate({ to: '/v/$venueSlug/sell', params: { venueSlug }, search: { pass: product.id, player: player.id } });
      return;
    }
    if (reason.trim() === '') {
      setError(t('passes.sell.reasonRequired'));
      return;
    }
    grant.mutate(
      { productId: product.id, body: { playerId: player.id, reason: reason.trim() } },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('passes.sell.granted', { name: product.version.name, player: playerName(player) }) });
          onDone();
        },
        onError: (failure) => setError(problem.text(failure, { fallback: t('passes.sell.refused') })),
      },
    );
  }

  return (
    <form className="console-form" onSubmit={submit} noValidate>
      <div className="console-pass-summary">
        <span className="console-pass-summary__price console-num">{format.money(product.version.price)}</span>
        <span className="console-subtle">
          {t('passes.minutes', { count: Math.round(product.version.durationSeconds / 60) })} · {validitySummary(t, product.version.validity)}
        </span>
      </div>
      {error !== undefined ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}

      {player === null ? (
        <div className="console-player-pick">
          <SearchField label={t('passes.sell.player')} placeholder={t('passes.sell.playerHint')} value={q} onChange={(e) => setQ(e.target.value)} />
          {search.isFetching ? <Skeleton shape="text" lines={2} /> : null}
          <ul className="console-player-pick__results">
            {(search.data?.data ?? []).map((candidate) => (
              <li key={candidate.id}>
                <button type="button" className="console-player-pick__result" onClick={() => setPlayer(candidate)}>
                  <Avatar name={playerName(candidate)} size="sm" decorative />
                  <span className="console-player-pick__name">{playerName(candidate)}</span>
                  {candidate.phoneNumber === null ? null : <span className="console-subtle">{candidate.phoneNumber}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="console-player-chosen">
          <Avatar name={playerName(player)} size="md" decorative />
          <span className="console-player-chosen__name">{playerName(player)}</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => setPlayer(null)}>
            {t('passes.sell.change')}
          </Button>
        </div>
      )}

      {canSell && canGrant ? (
        <SegmentedControl<'sell' | 'grant'>
          label={t('passes.sell.mode')}
          value={mode}
          onValueChange={setMode}
          block
          options={[
            { value: 'sell', label: t('passes.sell.sell') },
            { value: 'grant', label: t('passes.sell.grant') },
          ]}
        />
      ) : null}

      {mode === 'sell' ? (
        sellable ? (
          <p className="console-subtle">{t('passes.sell.checkoutHint')}</p>
        ) : (
          <Notice role="note">{t('passes.sell.notAtCounter')}</Notice>
        )
      ) : (
        <TextField label={t('passes.sell.reason')} required value={reason} onChange={(e) => setReason(e.target.value)} />
      )}

      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('passes.sell.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={grant.isPending} disabled={player === null || (mode === 'sell' && !sellable)}>
          {mode === 'sell' ? t('passes.sell.toCheckout') : t('passes.sell.confirmGrant')}
        </Button>
      </div>
    </form>
  );
}

function playerName(player: OrganizationPlayer): string {
  const full = [player.firstName, player.lastName].filter(Boolean).join(' ').trim();
  return player.displayName ?? player.username ?? (full !== '' ? full : (player.phoneNumber ?? player.id));
}

// -------------------------------------------------------------------------------------------------
// Pass holdings: find a player, then refund (F-PRC-04) or void (F-PRC-05) one of their holdings
// -------------------------------------------------------------------------------------------------

/** Find a player, then list and act on their pass holdings. */
function HoldingsManager({ organizationId, venueId, canRefund, canVoid }: { readonly organizationId: string; readonly venueId: string; readonly canRefund: boolean; readonly canVoid: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const api = useApi();
  const [q, setQ] = useState('');
  const [player, setPlayer] = useState<OrganizationPlayer | null>(null);
  const query = q.trim();
  const search = useQuery({
    queryKey: ['passes', 'holdings-player-search', organizationId, query],
    queryFn: () => api.listPlayers({ path: { organizationId }, query: { q: query, limit: 8 } }),
    enabled: query.length >= 2 && player === null,
  });
  const holdings = usePassHoldings(organizationId, player?.id);
  const myVenues = useMyVenues();
  const venueNames = useMemo(() => new Map((myVenues.data ?? []).map((venue) => [venue.id, venue.name])), [myVenues.data]);

  if (player === null) {
    return (
      <div className="console-player-pick">
        <SearchField label={t('passes.holdings.player')} placeholder={t('passes.holdings.playerHint')} value={q} onChange={(event) => setQ(event.target.value)} />
        {query.length >= 2 && (search.data?.data.length ?? 0) > 0 ? (
          <ul className="console-player-pick__results">
            {search.data?.data.map((candidate) => (
              <li key={candidate.id}>
                <button type="button" className="console-player-pick__result" onClick={() => setPlayer(candidate)}>
                  <Avatar name={playerName(candidate)} size="sm" decorative />
                  <span className="console-player-pick__name">{playerName(candidate)}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  return (
    <div className="console-holdings">
      <div className="console-player-chosen">
        <Avatar name={playerName(player)} size="md" decorative />
        <span className="console-player-chosen__name">{playerName(player)}</span>
        <Button variant="ghost" size="sm" onClick={() => { setPlayer(null); setQ(''); }}>
          {t('passes.holdings.change')}
        </Button>
      </div>
      {holdings.data === undefined ? (
        holdings.isError ? <Notice tone="danger" role="alert">{problem.text(holdings.error, { fallback: t('passes.holdings.unavailable') })}</Notice> : <Skeleton height="4rem" />
      ) : holdings.data.length === 0 ? (
        <Notice role="note">{t('passes.holdings.none')}</Notice>
      ) : (
        <ul className="console-holdings__list" aria-label={t('passes.holdings.listLabel')}>
          {holdings.data.map((holding) => (
            <li key={holding.id}>
              <HoldingRow organizationId={organizationId} playerId={player.id} holding={holding} canRefund={canRefund} canVoid={canVoid} venueId={venueId} venueNames={venueNames} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const HOLDING_REFUND_TENDERS = ['cash', 'counter_card', 'counter_upi'] as const;

/** One holding, with Refund (F-PRC-04) and Void (F-PRC-05) for an active one. */
function HoldingRow({
  organizationId,
  playerId,
  holding,
  canRefund,
  canVoid,
  venueId,
  venueNames,
}: {
  readonly organizationId: string;
  readonly playerId: string;
  readonly holding: PassHolding;
  readonly canRefund: boolean;
  readonly canVoid: boolean;
  /** The desk's venue: a holding valid only elsewhere says so (#709). */
  readonly venueId: string;
  readonly venueNames: ReadonlyMap<string, string>;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const { show } = useToast();
  const refund = useRefundPassHolding(organizationId, playerId);
  const voidHolding = useVoidPassHolding(organizationId, playerId);
  const [open, setOpen] = useState<'refund' | 'void' | null>(null);
  const [tender, setTender] = useState<(typeof HOLDING_REFUND_TENDERS)[number]>('cash');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const toggle = (which: 'refund' | 'void'): void => {
    setOpen(open === which ? null : which);
    setReason('');
    setError(undefined);
  };

  function submitRefund(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(undefined);
    refund.mutate(
      { holdingId: holding.id, body: { tender, ...(reason.trim() === '' ? {} : { reason: reason.trim() }) } },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('passes.holdings.refunded') });
          setOpen(null);
        },
        onError: (mutationError) => setError(problemCode(mutationError) === PROBLEM.conflict ? t('passes.holdings.notRefundable') : problem.text(mutationError, { fallback: t('passes.holdings.refundFailed') })),
      },
    );
  }

  function submitVoid(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (reason.trim() === '') {
      setError(t('passes.holdings.reasonRequired'));
      return;
    }
    setError(undefined);
    voidHolding.mutate(
      { holdingId: holding.id, body: { reason: reason.trim() } },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('passes.holdings.voided') });
          setOpen(null);
        },
        onError: (mutationError) => setError(problemCode(mutationError) === PROBLEM.conflict ? t('passes.holdings.notVoidable') : problem.text(mutationError, { fallback: t('passes.holdings.voidFailed') })),
      },
    );
  }

  const active = holding.status === 'active';
  // A holding keeps the scope it was sold under (#709): one scoped to venues says where, and whether it works here.
  const onlyAt = holding.scope.type === 'venues' ? (holding.scope.venueIds ?? []) : [];
  const validHere = onlyAt.length === 0 || onlyAt.includes(venueId as never);
  return (
    <div className="console-holdings__item">
      <div className="console-holdings__head">
        <span>{holding.name}</span>
        <Badge>{t(`passes.holdings.status.${holding.status}`)}</Badge>
        {onlyAt.length === 0 ? null : (
          <Badge tone={validHere ? 'neutral' : 'warn'}>
            {validHere ? t('passes.holdings.onlyAt', { venue: onlyAt.map((id) => venueNames.get(id) ?? t('passes.holdings.anotherVenue')).join(', ') }) : t('passes.holdings.notHere')}
          </Badge>
        )}
      </div>
      <p className="console-subtle">{t('passes.holdings.remaining', { time: format.duration(holding.remainingSeconds), price: format.money(holding.price) })}</p>
      {active && (canRefund || canVoid) ? (
        <div className="console-holdings__actions">
          {canRefund ? (
            <Button variant="secondary" size="sm" aria-expanded={open === 'refund'} onClick={() => toggle('refund')}>
              {t('passes.holdings.refund')}
            </Button>
          ) : null}
          {canVoid ? (
            <Button variant="secondary" size="sm" aria-expanded={open === 'void'} onClick={() => toggle('void')}>
              {t('passes.holdings.void')}
            </Button>
          ) : null}
        </div>
      ) : null}
      {open === 'refund' ? (
        <form className="console-form" noValidate onSubmit={submitRefund} aria-label={t('passes.holdings.refund')}>
          {error !== undefined ? (
            <Notice tone="danger" role="alert">
              {error}
            </Notice>
          ) : null}
          <SelectField
            label={t('passes.holdings.tender')}
            required
            options={HOLDING_REFUND_TENDERS.map((value) => ({ value, label: t(`passes.tender.${value}`) }))}
            value={tender}
            onValueChange={(value) => setTender(value as (typeof HOLDING_REFUND_TENDERS)[number])}
          />
          <TextField label={t('passes.holdings.reasonOptional')} showOptional value={reason} onChange={(event) => setReason(event.target.value)} />
          <Button type="submit" variant="primary" size="sm" busy={refund.isPending}>
            {t('passes.holdings.refundConfirm')}
          </Button>
        </form>
      ) : null}
      {open === 'void' ? (
        <form className="console-form" noValidate onSubmit={submitVoid} aria-label={t('passes.holdings.void')}>
          {error !== undefined ? (
            <Notice tone="danger" role="alert">
              {error}
            </Notice>
          ) : null}
          <TextField label={t('passes.holdings.reason')} required value={reason} onChange={(event) => setReason(event.target.value)} />
          <Button type="submit" variant="primary" size="sm" busy={voidHolding.isPending}>
            {t('passes.holdings.voidConfirm')}
          </Button>
        </form>
      ) : null}
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// Create a pass: three short steps
// -------------------------------------------------------------------------------------------------

interface CreateFormState {
  name: string;
  shortName: string;
  description: string;
  price: string;
  durationMinutes: string;
  validityType: 'days_after_purchase' | 'days_after_first_use';
  validityDays: string;
  classIds: readonly string[];
  /**
   * Where it can be used (owner 2026-09-30, #709): every venue of the organisation, or one venue (`scopeVenueId`).
   * `keep` is a pass made before the choice with a scope it cannot express — the network, or several venues.
   */
  scope: ScopeChoiceKind;
  scopeVenueId: string;
  label: (typeof LABELS)[number];
  /** Where it is sold (F-KIO-10): at the counter, on the kiosk at the PC, or both. */
  channels: readonly SaleChannel[];
  active: boolean;
  hasHours: boolean;
  windowDays: readonly string[];
  windowStart: string;
  windowEnd: string;
}

/** The sales channels the console offers (spec/06 §3.1); `app` and `web` arrive with the player apps. */
type SaleChannel = 'counter' | 'kiosk' | 'app' | 'web';
const OFFERED_CHANNELS = ['counter', 'kiosk'] as const;

/** Which fields each step owns, so a failed submit returns to the first step with an error. */
const STEP_FIELDS: readonly (readonly string[])[] = [
  ['name', 'shortName', 'price', 'durationMinutes', 'channels'],
  ['classIds', 'scopeVenue', 'windowDays'],
  ['validityDays', 'form'],
];

/** The form state for a new pass, or one seeded from the product being edited (its live version's terms). */
function formFor(editing: PassProduct | undefined, classOptions: readonly { id: string }[], venueId: string): CreateFormState {
  if (editing === undefined) {
    return {
      name: '',
      shortName: '',
      description: '',
      price: '',
      durationMinutes: '60',
      validityType: 'days_after_purchase',
      validityDays: '30',
      classIds: classOptions[0] === undefined ? [] : [classOptions[0].id],
      // A new pass works at every venue, as it always has; one venue is a choice (#709).
      scope: 'organization',
      scopeVenueId: venueId,
      label: 'standard',
      // A new pass is sold at the counter and on the kiosk: a player can buy it from their PC (F-KIO-10).
      channels: ['counter', 'kiosk'],
      active: true,
      hasHours: false,
      windowDays: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
      windowStart: '17:00',
      windowEnd: '23:00',
    };
  }
  const version = editing.version;
  const window = version.windows[0];
  // The form offers the two day-based validity types; a fixed-end pass reads as days-after-purchase here.
  const validityType = version.validity.type === 'days_after_first_use' ? 'days_after_first_use' : 'days_after_purchase';
  return {
    name: version.name,
    shortName: version.shortName,
    description: version.description ?? '',
    price: minorToMajor(version.price.amount, version.price.currency),
    durationMinutes: String(Math.round(version.durationSeconds / 60)),
    validityType,
    validityDays: String(version.validity.days ?? 30),
    classIds: version.stationClassIds,
    ...scopeFormOf(version.scope, venueId),
    label: version.label,
    channels: version.channels,
    active: editing.active,
    hasHours: version.windows.length > 0,
    windowDays: window?.days ?? ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
    windowStart: window?.start ?? '17:00',
    windowEnd: window?.end ?? '23:00',
  };
}

/** A stored scope as the form's choice: one named venue is `venue`; the network or several venues are kept as they are. */
function scopeFormOf(scope: PassScope, venueId: string): Pick<CreateFormState, 'scope' | 'scopeVenueId'> {
  if (scope.type === 'organization') return { scope: 'organization', scopeVenueId: venueId };
  if (scope.type === 'venues' && scope.venueIds?.length === 1) return { scope: 'venue', scopeVenueId: scope.venueIds[0] ?? venueId };
  return { scope: 'keep', scopeVenueId: venueId };
}

/** The request's fields -> the form's inputs, for a validation failure. */
const PASS_SERVER_FIELDS: Readonly<Record<string, string>> = {
  name: 'name',
  shortName: 'shortName',
  price: 'price',
  durationSeconds: 'durationMinutes',
  stationClassIds: 'classIds',
  channels: 'channels',
  'validity.days': 'validityDays',
  windows: 'windowDays',
  scope: 'scopeVenue',
};

function PassProductFlow({
  organizationId,
  venueId,
  currency,
  classOptions,
  editing,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly currency: string;
  readonly classOptions: readonly { id: string; name: string; colorToken: string }[];
  /** The product being edited, or absent when making a new one. */
  readonly editing?: PassProduct;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const { show } = useToast();
  const create = useCreatePassProduct(organizationId, venueId);
  const updateProduct = useUpdatePassProduct(organizationId, venueId);
  const serverErrors = useServerErrors();
  const [step, setStep] = useState(0);
  // The seed the form starts from; kept for diffing an edit — only the terms that changed are sent.
  const initial = useMemo(() => formFor(editing, classOptions, venueId), [editing, classOptions, venueId]);
  const myVenues = useMyVenues();
  const venues = useMemo(() => (myVenues.data ?? []).filter((venue) => venue.organizationId === organizationId), [myVenues.data, organizationId]);
  // What `keep` stands for, in words — only for a pass whose scope the two choices cannot express.
  const keepLabel =
    editing === undefined || initial.scope !== 'keep'
      ? undefined
      : editing.version.scope.type === 'network'
        ? t('pricing.scopeChoice.network')
        : t('pricing.scopeChoice.severalVenues', { count: editing.version.scope.venueIds?.length ?? 0 });
  const [form, setForm] = useState<CreateFormState>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function update<K extends keyof CreateFormState>(key: K, value: CreateFormState[K]): void {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function toggle(list: readonly string[], id: string): string[] {
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  }

  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    if (form.name.trim() === '') next['name'] = t('passes.create.required');
    if (form.shortName.trim() === '') next['shortName'] = t('passes.create.required');
    if (moneyFrom(form.price, currency) === null) next['price'] = t('passes.create.priceInvalid');
    const minutes = Number(form.durationMinutes);
    if (!Number.isInteger(minutes) || minutes < 15 || minutes > 43200) next['durationMinutes'] = t('passes.create.durationRange');
    if (form.classIds.length === 0) next['classIds'] = t('passes.create.classRequired');
    if (form.scope === 'venue' && form.scopeVenueId === '') next['scopeVenue'] = t('pricing.scopeChoice.venueRequired');
    if (form.channels.length === 0) next['channels'] = t('passes.create.channelRequired');
    if (form.hasHours && form.windowDays.length === 0) next['windowDays'] = t('passes.create.daysNeeded');
    const days = Number(form.validityDays);
    if (!Number.isInteger(days) || days < 1 || days > 730) next['validityDays'] = t('passes.create.daysRange');
    return next;
  }

  /** Continue: check this step's fields before the next one opens. */
  function next(): void {
    const all = validate();
    const mine = Object.fromEntries(Object.entries(all).filter(([key]) => STEP_FIELDS[step]?.includes(key)));
    setErrors(mine);
    if (Object.keys(mine).length === 0) setStep(step + 1);
  }

  /** The server refused: each field it names beside its input (the step that holds it opens), the rest as a notice. */
  function failed(failure: unknown, fallback: string): void {
    const refused = serverErrors(failure, fallback, PASS_SERVER_FIELDS);
    setErrors(refused);
    const first = STEP_FIELDS.findIndex((fields) => fields.some((field) => field in refused));
    if (first >= 0) setStep(first);
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (step < 2) {
      next();
      return;
    }
    const all = validate();
    setErrors(all);
    const price = moneyFrom(form.price, currency);
    if (Object.keys(all).length > 0 || price === null) {
      const first = STEP_FIELDS.findIndex((fields) => fields.some((field) => field in all));
      if (first >= 0) setStep(first);
      return;
    }
    const minutes = Number(form.durationMinutes);
    const days = Number(form.validityDays);
    const scope: PassScope =
      form.scope === 'venue'
        ? { type: 'venues', venueIds: [form.scopeVenueId as never] }
        : form.scope === 'keep' && editing !== undefined
          ? editing.version.scope
          : { type: 'organization' };
    const validity: PassValidity = { type: form.validityType, days };
    const windows: CreatePassProductRequest['windows'] = form.hasHours ? [{ days: form.windowDays as never, start: form.windowStart, end: form.windowEnd }] : [];

    // Editing: send only the terms that changed (a term change mints a new version server-side); a
    // change to `active` alone stays in place. Saving with nothing changed never leaves the browser.
    if (editing !== undefined) {
      const patch: UpdatePassProductRequest = {};
      if (form.name.trim() !== initial.name.trim()) patch.name = form.name.trim();
      if (form.shortName.trim() !== initial.shortName.trim()) patch.shortName = form.shortName.trim();
      if (form.description.trim() !== initial.description.trim()) patch.description = form.description.trim();
      if (form.price !== initial.price) patch.price = price;
      if (form.durationMinutes !== initial.durationMinutes) patch.durationSeconds = minutes * 60;
      if (form.validityType !== initial.validityType || form.validityDays !== initial.validityDays) patch.validity = validity;
      if (JSON.stringify([...form.classIds].sort()) !== JSON.stringify([...initial.classIds].sort())) patch.stationClassIds = form.classIds as never;
      if (form.scope !== initial.scope || (form.scope === 'venue' && form.scopeVenueId !== initial.scopeVenueId)) patch.scope = scope;
      if (form.hasHours !== initial.hasHours || JSON.stringify(form.windowDays) !== JSON.stringify(initial.windowDays) || form.windowStart !== initial.windowStart || form.windowEnd !== initial.windowEnd) patch.windows = windows;
      if (form.label !== initial.label) patch.label = form.label;
      if (JSON.stringify([...form.channels].sort()) !== JSON.stringify([...initial.channels].sort())) patch.channels = [...form.channels] as never;
      if (form.active !== initial.active) patch.active = form.active;
      if (Object.keys(patch).length === 0) {
        setErrors({ form: t('passes.edit.noChanges') });
        return;
      }
      updateProduct.mutate(
        { productId: editing.id, body: patch },
        {
          onSuccess: () => {
            show({ tone: 'ok', title: t('passes.edit.done', { name: form.name.trim() }) });
            onDone();
          },
          onError: (failure) => failed(failure, t('passes.edit.refused')),
        },
      );
      return;
    }

    const body: CreatePassProductRequest = {
      name: form.name.trim(),
      shortName: form.shortName.trim(),
      ...(form.description.trim() === '' ? {} : { description: form.description.trim() }),
      price,
      durationSeconds: minutes * 60,
      validity,
      stationClassIds: form.classIds as never,
      scope,
      windows,
      label: form.label,
      channels: [...form.channels] as never,
      active: form.active,
      sortOrder: 0,
    };
    create.mutate(body, {
      onSuccess: () => {
        show({ tone: 'ok', title: t('passes.create.done', { name: form.name.trim() }) });
        onDone();
      },
      onError: (failure) => failed(failure, t('passes.create.refused')),
    });
  }

  const tint = classOptions.find((option) => option.id === form.classIds[0])?.colorToken;
  // The preview shows the price as it will read on the pass: venue-formatted money, not the raw input.
  const previewPrice = moneyFrom(form.price, currency);

  return (
    <div className="console-stepper-flow">
      <Stepper
        current={step}
        onStepSelect={(index) => (index < step ? setStep(index) : undefined)}
        steps={[
          { id: 'basics', label: t('passes.create.stepBasics') },
          { id: 'when', label: t('passes.create.stepWhen') },
          { id: 'validity', label: t('passes.create.stepValidity') },
        ]}
      />
      <section className="console-dialog-preview" aria-label={t('passes.create.preview')}>
        <span className="console-eyebrow">{t('passes.create.preview')}</span>
        <div className="console-pass-preview">
          <div className="console-pass-card__head">
            <h3 className="console-pass-card__name">{form.name.trim() === '' ? t('passes.create.namePlaceholder') : form.name.trim()}</h3>
            {form.label === 'standard' ? null : <Badge tone="neutral">{t(`passes.label.${form.label}`)}</Badge>}
            <span className="console-pass-preview__price console-num">{previewPrice === null ? '—' : format.money(previewPrice)}</span>
          </div>
          <WhenItWorksBand windows={form.hasHours ? [{ days: form.windowDays as never, start: form.windowStart, end: form.windowEnd }] : []} {...(tint === undefined ? {} : { tint })} />
        </div>
      </section>
      <form className="console-form" onSubmit={submit} noValidate>
        {errors['form'] !== undefined ? (
          <Notice tone="danger" role="alert">
            {errors['form']}
          </Notice>
        ) : null}

        {step === 0 ? (
          <FormSection key="basics"
            more={
              <SelectField label={t('passes.create.label')} value={form.label} onValueChange={(value) => update('label', value as CreateFormState['label'])} options={LABELS.map((label) => ({ value: label, label: t(`passes.label.${label}`) }))} />
            }
          >
            <TextField label={t('passes.create.name')} required value={form.name} onChange={(e) => update('name', e.target.value)} error={errors['name']} />
            <TextField label={t('passes.create.shortName')} description={t('passes.create.shortNameHint')} required value={form.shortName} onChange={(e) => update('shortName', e.target.value)} error={errors['shortName']} />
            <div className="console-form__row">
              <TextField label={t('passes.create.price', { currency })} required numeric inputMode="decimal" value={form.price} onChange={(e) => update('price', e.target.value)} error={errors['price']} />
              <TextField label={t('passes.create.duration')} required numeric inputMode="numeric" value={form.durationMinutes} onChange={(e) => update('durationMinutes', e.target.value)} error={errors['durationMinutes']} />
            </div>
            <fieldset className="console-choice-group">
              <legend className="clav-field__label">{t('passes.create.channels')}</legend>
              <div className="console-chips">
                {OFFERED_CHANNELS.map((channel) => (
                  <Chip key={channel} selected={form.channels.includes(channel)} onSelectedChange={() => update('channels', toggle(form.channels, channel) as SaleChannel[])}>
                    {t(`passes.channel.${channel}`)}
                  </Chip>
                ))}
              </div>
              <p className="clav-field__description">{t('passes.create.channelsHint')}</p>
              {errors['channels'] !== undefined ? <p className="clav-field__error">{errors['channels']}</p> : null}
            </fieldset>
          </FormSection>
        ) : step === 1 ? (
          <>
            <fieldset className="console-choice-group">
              <legend className="clav-field__label">{t('passes.create.classes')}</legend>
              {classOptions.length === 0 ? (
                <p className="console-muted">{t('passes.create.noClasses')}</p>
              ) : (
                <div className="console-chips">
                  {classOptions.map((option) => (
                    <Chip
                      key={option.id}
                      selected={form.classIds.includes(option.id)}
                      onSelectedChange={() => update('classIds', toggle(form.classIds, option.id))}
                      icon={<span className="console-chip-dot" style={swatchStyle(option.colorToken as never)} />}
                    >
                      {option.name}
                    </Chip>
                  ))}
                </div>
              )}
              {errors['classIds'] !== undefined ? <p className="clav-field__error">{errors['classIds']}</p> : null}
            </fieldset>
            <ScopeChoice
              label={t('passes.create.scope')}
              kind={form.scope}
              venueId={form.scopeVenueId}
              venues={venues}
              onKindChange={(kind) => update('scope', kind)}
              onVenueChange={(id) => update('scopeVenueId', id)}
              {...(keepLabel === undefined ? {} : { keepLabel })}
              {...(errors['scopeVenue'] === undefined ? {} : { error: errors['scopeVenue'] })}
            />
            <fieldset className="console-choice-group">
              <legend className="clav-field__label">{t('passes.create.when')}</legend>
              <Switch checked={form.hasHours} onCheckedChange={(value) => update('hasHours', value)} label={t('passes.create.setHours')} />
              {form.hasHours ? (
                <>
                  <div className="console-chips console-chips--days">
                    {DAYS.map((day) => (
                      <Chip key={day} selected={form.windowDays.includes(day)} onSelectedChange={() => update('windowDays', toggle(form.windowDays, day))}>
                        {t(`passes.day.${day}`)}
                      </Chip>
                    ))}
                  </div>
                  {errors['windowDays'] !== undefined ? <p className="clav-field__error">{errors['windowDays']}</p> : null}
                  <div className="console-form__row">
                    <TextField label={t('passes.create.start')} value={form.windowStart} onChange={(e) => update('windowStart', e.target.value)} description={t('passes.create.timeHint')} />
                    <TextField label={t('passes.create.end')} value={form.windowEnd} onChange={(e) => update('windowEnd', e.target.value)} description={t('passes.create.timeHint')} />
                  </div>
                </>
              ) : (
                <p className="console-muted">{t('passes.create.anyTimeHint')}</p>
              )}
            </fieldset>
          </>
        ) : (
          <FormSection key="validity"
            more={
              <>
                <TextField label={t('passes.create.description')} showOptional value={form.description} onChange={(e) => update('description', e.target.value)} />
                <Switch checked={form.active} onCheckedChange={(value) => update('active', value)} label={t('passes.create.active')} />
              </>
            }
          >
            <div className="console-form__row">
              <SelectField
                label={t('passes.create.validity')}
                value={form.validityType}
                onValueChange={(value) => update('validityType', value as CreateFormState['validityType'])}
                options={[
                  { value: 'days_after_purchase', label: t('passes.validityType.afterPurchase') },
                  { value: 'days_after_first_use', label: t('passes.validityType.afterFirstUse') },
                ]}
              />
              <TextField label={t('passes.create.days')} required numeric inputMode="numeric" value={form.validityDays} onChange={(e) => update('validityDays', e.target.value)} error={errors['validityDays']} />
            </div>
          </FormSection>
        )}

        <div className="console-form__actions console-form__actions--end">
          {step === 0 ? (
            <Button type="button" variant="ghost" onClick={onDone}>
              {t('passes.create.cancel')}
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={() => setStep(step - 1)}>
              {t('common.back')}
            </Button>
          )}
          <Button type="submit" variant="primary" busy={create.isPending || updateProduct.isPending}>
            {step < 2 ? t('common.next') : editing === undefined ? t('passes.create.create') : t('passes.edit.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
